/**
 * Game-wide constants. This file imports nothing, on purpose.
 */

export const Resolution = {
	Width: 1920,
	Height: 1080
};

export const SimulationConstants = {
	Antibodies: 1,
	//VirusMutationCountInceaseAmount: 1,
	SimulationClientLagBudgetMS: 0,
	MaxRoundsByTrainingLevel: [5, 7, 10, 12],
	MinRoundForReplaySave: 5,
	BetweenWaveDelay: 4,
	BetweenLaneDelay: 2,
	FirstWaveDelay: 1,
	SpaceBetweenWaves: 20,
}

/**
 * Where the client looks up every other backend URL.
 *
 * NOTE: Change this carefully, production clients check here for our APIs.
 */
export const WebServicesEndpointUrl = 'https://services.autoimmunegame.com/endpoints.json';

export const ServicesFetchTimeout = 10000;

export const AutosaveName = "autosave";
export const MultiplayerAutosaveName = "mp-autosave";

export const Multiplayer = {
	MaxPlayers: 2,
	MaxRememberedUploadedReplays: 10,
	PlayerTerritoryRadius: 60,
	PlayerSpawnPointOffset: 90, //TerritoryRadius*1.5
	RewardRoundBeforeFinalCount: 4,
	EarliestRewardRound: 2, //0 indexed
	ServerIP: "localhost:5454", //For when running as client/server mode, mostly for dev
	ClientReconnectMaxAttempts: 20,

	WaitForConnectionTimeout: 10 * 1000 * 60,
	SignallingConnectTimeout: 10000,
	SteamChannelConnectTimeout: 10000,
	SteamReconnectPollTimeout: 30000,
	ReconnectRetryDelay: 3000,
	ServicesFetchTimeout,
	ServerAckTimeout: 15000,

	AutosaveName,
	MultiplayerAutosaveName,
	RetryConnectionTimeout: 1000 * 60,
	DataChannelTimeout: 30000,
	WebrtcClientHandshakeTimeout: 8000
};

// export function totalPlayerCount() {
// 	return Multiplayer.NumberOfBots + Multiplayer.NumberOfPlayers;
// }

export const VirusSpawnerConstants = {
	GrowthRate: 1,
	StartingVirusCount: 1,
	StartingGrowthRate: 2,
	SpawnIntervalMin: 1,
	SpawnIntervalMax: 2,
	LaneCount: 3,
	PossibleLaneAngles: [0, 45, 90, 135, 180, -45, -135],
	SpawnerDistanceByAngle: [80, 70, 60, 70, 80, 70, 70],
	NewVirusCountPerRound: [2, 2, 3, 3, 5, 6, 8, 9, 12, 14, 18, 20],
	NewVirusCountPerRoundInTutorial: [2, 2, 3, 3, 4, 5, 5, 7, 7, 8, 9, 10],
	BattleRoundGrowthProgression: [0.5, 1, 1, 1],
	LaneCountByTrainingLevel: [1, 2, 3, 3],
	EndlessModeVirusMultiplierPerRound: 1.5,

}

export const ShopConstants = {
	ShopDurationMS: 60 * 1000,
	RecapStateFirstStageMS: 1000,
	RecapStateSecondStageMS: 3000,
	RecapStateTotalMS: 0, //updated below
	RoundIncreaseTimer: 1000 * 12,
	MaxRoundTime: 1000 * 60,
	MaxTimer: 10, // Seconds
	PlacementRadius: 46,
	SellBuffer: 5,
	PromotionPopDelay: 200,
	PromotionPopScale: 1.2,
	PromotionNumber: 3,
	MovementStepMeters: 0.001,
	RotationStepDegrees: 5,
	RewardChance: 0.01,
	CellsPerPage: 6,
	HideOtherPlayerShops: false,
	ShopExitTransitionTimeSeconds: 1.5,
}
ShopConstants.RecapStateTotalMS = ShopConstants.RecapStateFirstStageMS + ShopConstants.RecapStateSecondStageMS;

export const ControlConstants = {
	UseMultitouchSelection: false,
}

const physicsHeight = 90;
export const PhysicsConstants = {
	Width: physicsHeight * (Resolution.Width / Resolution.Height),
	Height: physicsHeight,
	StepRate: 1 / 60,
	MetersToPixelsConversion: Resolution.Height / physicsHeight,
	PixelsToMetersConversion: physicsHeight / Resolution.Height,
	DefaultDamping: 0.5,
	FriendlyCollisionStunSeconds: 0.5,
	ExplosianPropagationSpeed: 0.02,
};

export const Timings = {
	VirusExpirationSecondPadding: 2,
	VirusExpirationSeconds: 12,
	VirusExpirationSteps: 0 //set later
}

Timings.VirusExpirationSteps = Timings.VirusExpirationSeconds * (1 / PhysicsConstants.StepRate);

export const ResourceConstants = {
	ConsumeSpeed: 0.75
}

export enum CellCategory {
	None,
	BCell,
	TCell
}


export enum CollisionCategory {
	None = 0x0000,
	Virus = 0x0002,
	Antibody = 0x0004,
	Cell = 0x0008,
	Virophage = 0x0010,
	Cytokine = 0x0020,
	All = 0x001F
};

export enum WeightClass {
	Light = 0.5,
	Normal = 1.0,
	Heavy = 10.0,
	SuperHeavy = 100.0
}

export const RenderEffects = {
	DisplacementScale: 10,
	HealthPopupFadeSeconds: 3,
	VignetteApertureMsPerPixel: 1,
	VignetteApertureFocusedSmall: ShopConstants.PlacementRadius / 4,
	VignetteApertureFocused: ShopConstants.PlacementRadius - 6,
	VignetteApertureFull: ShopConstants.PlacementRadius * 1.6,
	HitStopDurationSeconds: 0.15,
	DeathScaleSize: 1.5,
	DeathScaleTime: 150,
	SquashAndStretchTotalDuration: 250,
	SquashAndStretchRadiusPercentage: 0.4,
	DirectionIndicatorSideLength: 25
};

export const OverlayConstants = {
	CooldownBarFullColor: 0x0000ff,
	CooldownBarEmptyColor: 0x999999,
	CooldownBarWidth: 50,
	CooldownBarHeight: 10,
	ConsumeProgressBarHeight: 7,
	ConsumeProgressBarColor: 0xFFA500,
}

export const OverlayText = {
	FloatingDefaultDuration: 1.0,
	FloatingFadeOutDuration: 0.2,
	OverlayTextStyle: { align: 'left', fontStyle: 'normal', fontSize: '24px', fill: 0xffffff, stroke: 'black', strokeThickness: 2 },
	OverlayFloatTextStyle: { align: 'left', fontStyle: 'normal', fontSize: '20px', fill: 0xffff88, stroke: 'black', strokeThickness: 2 },
	OverlayElectricityTextStyle: { align: 'left', fontStyle: 'normal', fontSize: '24px', fill: 0x000000, stroke: 'black', strokeThickness: 0 },
}

export const ServerConstants = {
	FramesBetweenStateSync: 60
}

export const Controls = {
	KeyStep: 20,
	ZoomFactor: 0.001
}

export const Camera = {
	StepMeters: 10,
	ZoomScaleLevel1: 1,
	ZoomScaleLevel2: 3 / 4,
	ZoomScaleLevel3SinglePlayer: 0.4,
	ZoomScaleLevel3Multiplayer: 0.4,

	StandardFocalDistance: 0.75,
	StandardFocalFalloff: 0.4,
	FocalDistanceRackSpeed: 0.02,

	ShopActivationDistance: 300,
	MaxRadiusGrid: 20,
	KeyPressStep: 0.3,

	InfectedSpawnAutoFocusNoticeSeconds: SimulationConstants.BetweenLaneDelay,

	StartGameZoomOutTotalDurationSeconds: 1.8
}

export const Background = {
	WaterImprintAddedScale: 0.2,
	//The trails texture is screen sized but covers a fixed region of the world, centered on the world origin.
	//It needs to be small enough to cover the whole multiplayer arena (viruses are limited to ~2x PlayerTerritoryRadius from the origin)
	TrailsTextureScaleFactor: 0.3,
	//Fraction of the screen size the trails texture extends past a screen centered on the world origin, on each side. Set below
	TrailsTexturePadding: 0,
}
Background.TrailsTexturePadding = (0.5 / Background.TrailsTextureScaleFactor) - 0.5;

export const PlayerConstants = {
	StartingHpByTrainingLevel: [14, 30, 30, 30],
	StartingFunds: 3, // There's currently a bug where the income happens at the start of the round, so this is reduced by one to account for that
	StartingLevel: 1,
	StartingExp: 0,
	MaxLevel: 5,
	Income: 1,
	ExperienceLevels: [3, 6, 12, 18, 24], //[3, 3, 6, 6] //[1, 2, 5]
	CapacityPerLevel: [4, 8, 12, 16, 20],
	//MarketSizePerLevel: [4, 4, 4, 4, 4],
	MarketSizePerLevel: [2, 3, 4, 4, 5], //TODO: change to this after playtest
	ExperiencePerRound: 1,
	ExperienceCost: 3,
	ExperienceGain: 1,
	RefreshShopCost: 1,
	ImmuneCellCost: 2,
	MaxStartingMoney: 10,
	NumberOfRewardOptions: 2,
}

export const BehaviorsConstants = {
	MaxVirophageSize: 5,
	MinCooldown: 0.2,
	TagProteinAttractRange: 15,
	TagProteinSuspendTime: 1.5,
	SeekerReturnHomeTimeout: 1.1,
	ConductionRange: 15,
}

export const Speeds = {
	Normal: 15,
	Fast: 25,
	Slow: 10,
	VerySlow: 5,
	NormalImpulse: 250,
	FastImpulse: 500,
	WeakImpulse: 100,
	CameraNormalSpeed: 2,
	CameraSlowSpeed: 6,
	CameraVerySlowSpeed: 10,
}

export const Chaining = {
	ChainRange: 6,
	ChainWidthPixels: 16
}

export const UIConstants = {
	GainMoneyAnimationSeconds: 1,
	ChecklistTransitionSeconds: 0.3,
	ModalFadeIn: 0.1,
	ShowWishlist: false,
}

export const DefaultMembraneColor: number = 0xffff00;

export enum CameraZoomLevel {
	Normal = 0,
	Medium = 1,
	Far = 2
}

export const MouseControls = {
	LeftButton: 0,
	MiddleButton: 1,
	RightButton: 2,
}

export enum SteamAchievements {
	PlayFirstGame = "play_first_game",
	FinishTraining = "finish_training",
	WinFirstGame = "win_first_game",
	TriedMultiplayer = "tried_multiplayer",
	LargeHealth = "large_health",
	LargeMoney = "large_money",

	//TODO: Test these!
	Round4Endless = "endless_survival_1",
	StayUnder10Cells = "small_cell_count",
	ReachedRank1 = "reached_rank_1",
	ReachedRank2 = "reached_rank_2",
	ReachedRank3 = "reached_rank_3",
	ReachedRank4 = "reached_rank_4",
	ReachedRank5 = "reached_rank_5",
	Connector = "connector",
	BigExplosion = "big_explosion",
	BigShock = "big_shock",
	Summoner = "summoner",
}

/** The achievements shown on the in game checklist (some others are steam only) */
export const ChecklistAchievements: string[] = [
	SteamAchievements.WinFirstGame,
	SteamAchievements.Round4Endless,
	SteamAchievements.StayUnder10Cells,
	SteamAchievements.ReachedRank1,
	SteamAchievements.ReachedRank2,
	SteamAchievements.ReachedRank3,
	SteamAchievements.ReachedRank4,
	SteamAchievements.ReachedRank5,
	SteamAchievements.Summoner,
	SteamAchievements.BigShock,
	SteamAchievements.BigExplosion,
	SteamAchievements.Connector,
];

export const RankPerTier = [0, 3, 6, 9, 12, 15, 18]

export const Optimizations = {
	PhysicsBodyPooling: false,  //in practice doesn't seem to help much, but might be worth it for garbage or slow frames or something in the future
}
